import {PolymerElement, html} from '@polymer/polymer';
import {afterNextRender} from '@polymer/polymer/lib/utils/render-status.js';
import {ScopeBinding} from '../behaviors/keli-binding-behavior.js';
import {InputBehavior} from '../behaviors/input-behavior.js';
import {CodemirrorSharedStylesHTML} from '../shared-styles/codemirror.js';
import {loadDynamicDependency} from '../../javascripts/modules/loadDynamicDependency.js';
import {ResizeObserverBehavior} from '../behaviors/resize-observer-behavior.js';

class KeliCodearea extends ScopeBinding(InputBehavior(ResizeObserverBehavior(PolymerElement))) {
  static get template() {
    return html`
${CodemirrorSharedStylesHTML}
<style>
:host {
  display: block;
  min-height: 10em;
  padding: 0;
  height: auto !important;
  z-index: 1;
}

.CodeMirror {
  height: 100% !important;
  width: 100%;
  position: absolute !important;
  top: 0;
  bottom: 0;
}

#input {
  display: none;
}

:host::after {
  content: none;
}
</style>`;
  }

  static get is() {
    return 'keli-codearea';
  }

  static get properties() {
    return {
      codeKeyMap: {type: String, scopevar: true, observer: 'codeKeyMapChanged'},
      codeTheme: {type: String, scopevar: true, observer: 'codeThemeChanged'},
      commitInterval: {type: Number, scopevar: true},
      cursorCh: {type: Number, scopevar: {options: {view: true}}, observer: 'cursorChChanged'},
      cursorLine: {type: Number, scopevar: {options: {view: true}}, observer: 'cursorLineChanged'},
      isBlockPanelElement: {type: Boolean, value: true},
      keliControlTypes: {type: Array, value: ['code']}
    };
  }

  constructor() {
    super();
    this.addEmitToKeli(['keliCodeShowHelp']);
    this.addOnKeli({
      'showhelpforfunction': 'showHelpForFunction',
      'formatselectedblock': 'formatSelectedBlock'
    });
  }

  async connectedCallback() {
    await super.connectedCallback();

    await loadDynamicDependency([
      {global: window.CodeMirror, path: '/javascripts/codemirror-package.js'}
    ]);
    this.createCodeMirror();

    this.autoCommitter = window.setInterval(() => {
      if (!this.readOnly) {
        this._keliAccept();
      }
    }, this.commitInterval || 30000);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.mirror = undefined;
    if (this.autoCommitter) {
      window.clearInterval(this.autoCommitter);
    }
  }

  handleElementResized() {
    if (this.mirror) {
      let scrollInfo = this.mirror.getScrollInfo();
      if (scrollInfo.height > 0 && scrollInfo.width > 0) {
        this.mirror.refresh();
      }
    }
  }

  focusInput() {
    if (this.mirror && !this.mirror.hasFocus()) {
      this.mirror.focus();
    }
  }

  currentValueChanged(newValue, oldValue) {
    if (this.mirror) {
      const editorValue = this.mirror.getValue();
      if (newValue !== editorValue) {
        afterNextRender(this, () => {
          const oldScrollInfo = this.mirror.getScrollInfo();
          this.mirror.setValue(newValue);

          // If it's the initial load, look at the scope variables.
          if (oldValue === undefined) {
            this.scrollTo(this.cursorLine, this.cursorCh);
          } else {
            this.mirror.scrollTo(oldScrollInfo.left, oldScrollInfo.top);
            this.mirror.refresh();
          }
        });
      }
    }
  }

  cursorLineChanged(newValue) {
    this.scrollTo(newValue);
  }

  cursorChChanged(newValue) {
    this.scrollTo(this.cursorLine, newValue);
  }

  codeThemeChanged(newValue) {
    if (this.mirror && this.mirror.setOption) {
      this.mirror.setOption('theme', newValue);
    }
  }

  codeKeyMapChanged(newValue) {
    if (this.mirror && this.mirror.setOption) {
      this.mirror.setOption('keyMap', newValue);
    }
  }

  readOnlyChanged(newValue) {
    if (this.mirror) {
      this.mirror.setOption('readOnly', newValue);
    }
  }

  async showHelpForFunction() {
    if (!this.mirror) {
      return;
    }
    const token = this.mirror.getTokenAt(this.mirror.getCursor());
    let type;
    switch (token.type) {
      case 'variable':
        type = 'function';
        break;
      case 'variable-3':
        type = 'property';
        break;
      case 'def':
        if (token.state.lexical.type === 'block') {
          type = 'function';
        }
        break;
    }
    if (type) {
      this.dispatchEvent(new CustomEvent('keliCodeShowHelp', {detail: {variable: token.string, type: type}}));
    }
  }

  async formatSelectedBlock() {
    if (!this.mirror) {
      return;
    }
    if (typeof window.js_beautify === 'undefined' || !this.mirror.somethingSelected()) {
      return;
    }
    this.mirror.replaceSelection(window.js_beautify(this.mirror.getSelection(), {
      indent_size: 2,
      indent_char: ' ',
      max_preserve_newlines: 2,
      space_after_anon_function: false,
      brace_style: 'collapse',
      space_before_conditional: true
    }));
  }

  createCodeMirror() {
    if (!window.CodeMirror) {
      return;
    }

    let mixedHinting = (mirror, options) => {
      const anyHint = window.CodeMirror.hint.anyword(mirror, options);
      const jshint = window.CodeMirror.hint.javascript(mirror, options);
      const words = new Set([...((anyHint && anyHint.list) || []), ...((jshint && jshint.list) || [])]);
      if (words.size > 0) {
        let source;
        if (anyHint && anyHint.from) {
          source = anyHint;
        } else if (jshint && jshint.from) {
          source = jshint;
        }
        if (!source) {
          return;
        }
        return {
          list: Array.from(words),
          from: source.from,
          to: source.to
        }
      }
    };

    const saveContents = () => {
      this._keliAccept();
    };

    this.mirror = new window.CodeMirror(this.shadowRoot, {
      mode: {name: 'keli', globalVars: true},
      theme: this.codeTheme || 'darko',
      styleActiveLine: true,
      tabSize: 2,
      lineNumbers: true,
      foldGutter: true,
      value: this.currentValue || '',
      readOnly: this.readOnly || false,
      keyMap: this.codeKeyMap || 'default',
      extraKeys: {
        'Ctrl-S': saveContents,
        'Cmd-S': saveContents
      },
      gutters: ['CodeMirror-linenumbers', 'CodeMirror-foldgutter'],
      autofocus: true,
      matchBrackets: true,
      showTrailingSpace: true
    });

    this.mirror.on('blur', saveContents);
    this.mirror.on('focus', () => {
      this.dispatchEvent(new CustomEvent('setPanelActiveElement', {
        bubbles: true,
        composed: true,
        detail: {element: this}
      }));
    });
    this.mirror.on('change', (mirror, e) => {
      if (!this.readOnly && e.origin !== 'setValue') {
        this.fireKeliAccepted = true;
        this.dispatchEvent(new CustomEvent('keliUserChange', {
          bubbles: true,
          composed: true,
          detail: {element: this, oldValue: e.from, newValue: e.to}
        }));
      }
    });
    this.mirror.on('cursorActivity', (mirror) => {
      if (mirror.getViewport && mirror.getViewport().to !== 0) {
        const position = mirror.getCursor();
        this.cursorLine = position.line;
        this.cursorCh = position.ch;
      }
    });
    this.mirror.on('inputRead', (mirror) => {
      if (!mirror.state.completionActive) {
        const cursor = mirror.getCursor();
        const token = mirror.getTokenAt(cursor);
        if (token.type && !['comment', 'error'].includes(token.type)) {
          window.CodeMirror.commands.autocomplete(mirror, null, {
            completeSingle: false,
            hint: mixedHinting
          });
        }
      }
    });
    this.mirror.on('keyHandled', (mirror, key, event) => {
      if (['Alt-B'].indexOf(key) === -1) {
        event.stopPropagation();
      }
    });
    this.mirror.addKeyMap({
      'Tab': (cm) => {
        cm.indentSelection('add');
      },
      'Shift-Tab': (cm) => {
        cm.indentSelection('subtract');
      }
    });

    afterNextRender(this, () => {
      this.scrollTo(this.cursorLine, this.cursorCh);
    });
  }

  scrollTo(line, ch) {
    if (!this.mirror || !this.mirror.getCursor) {
      return;
    }
    const position = this.mirror.getCursor();
    if (line === undefined) {
      line = position.line;
    }
    if (ch === undefined) {
      ch = position.ch;
    }

    if (this.mirror.lineCount() < line) {
      return;
    }

    if (line !== position.line || ch !== position.ch) {
      const scrollInfo = this.mirror.getScrollInfo();
      const newPos = {line: line, ch: ch};
      this.mirror.setCursor(newPos, {scroll: false});
      this.mirror.scrollIntoView(null, Math.floor(scrollInfo.clientHeight * 0.5));
    }
  }

  keliAccept() {
    if (!this.fireKeliAccepted) {
      return false;
    }
    if (this.mirror) {
      this.currentValue = this.mirror.getValue();
    }
  }
}

customElements.define(KeliCodearea.is, KeliCodearea);
export {KeliCodearea};
